import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { extent } from 'd3';
import { xFindClosestIndex } from 'ml-spectra-processing';
import { matrixToBoxPlot } from 'nmr-processing';
import { useMemo } from 'react';
import { useChartData } from '../../context/ChartContext';
import { useScaleChecked } from '../../context/ScaleContext';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import { PathBuilder } from '../../utility/PathBuilder';
import { getYScaleWithRation } from '../utilities/scale';
import { sliceArrayForDomain, useMatrix } from './useMatrix';
function useYScale(scaleRatio, yDomain) {
    const { margin, height } = useChartData();
    return getYScaleWithRation({
        height,
        yDomain,
        scaleRatio,
        margin,
    });
}
function usePath(pathPoints, scaleRatio, yDomain) {
    const { scaleX } = useScaleChecked();
    const scaleY = useYScale(scaleRatio, yDomain);
    const pathBuilder = new PathBuilder();
    const xScaler = scaleX();
    pathBuilder.moveTo(xScaler(pathPoints.x[0]), scaleY(pathPoints.y[0]));
    for (let i = 1; i < pathPoints.x.length; i++) {
        pathBuilder.lineTo(xScaler(pathPoints.x[i]), scaleY(pathPoints.y[i]));
    }
    return pathBuilder.toString();
}
function useAreaPath(pathPoints, scaleRatio, yDomain) {
    const { scaleX } = useScaleChecked();
    const scaleY = useYScale(scaleRatio, yDomain);
    const pathBuilder = new PathBuilder();
    const pathBuilder2 = new PathBuilder();
    const xScaler = scaleX();
    pathBuilder.moveTo(xScaler(pathPoints.x[0]), scaleY(pathPoints.y1[0]));
    for (let i = 1; i < pathPoints.x.length; i++) {
        pathBuilder.lineTo(xScaler(pathPoints.x[i]), scaleY(pathPoints.y1[i]));
    }
    for (let i = pathPoints.x.length - 1; i >= 0; i--) {
        pathBuilder2.lineTo(xScaler(pathPoints.x[i]), scaleY(pathPoints.y2[i]));
    }
    return pathBuilder.concatPath(pathBuilder2);
}
function useBoxPlot() {
    const matrix = useMatrix();
    const { xDomain: [from, to], } = useChartData();
    return useMemo(() => {
        if (!matrix)
            return null;
        const { x, matrixY } = matrix;
        const { max, min, median, q1, q3 } = matrixToBoxPlot(matrixY);
        const fromIndex = xFindClosestIndex(x, from);
        const toIndex = xFindClosestIndex(x, to);
        const yDomain = extent(median);
        return {
            x: sliceArrayForDomain(x, { fromIndex, toIndex }),
            max: sliceArrayForDomain(max, { fromIndex, toIndex }),
            min: sliceArrayForDomain(min, { fromIndex, toIndex }),
            median: sliceArrayForDomain(median, { fromIndex, toIndex }),
            q1: sliceArrayForDomain(q1, { fromIndex, toIndex }),
            q3: sliceArrayForDomain(q3, { fromIndex, toIndex }),
            yDomain,
        };
    }, [from, matrix, to]);
}
export function Boxplot() {
    const { view: { spectra: { activeTab }, }, } = useChartData();
    const options = usePanelPreferences('matrixGeneration', activeTab);
    if (!options)
        return;
    const { scaleRatio, showBoxPlot } = options;
    if (!showBoxPlot)
        return null;
    return _jsx(InnerBoxplot, { scaleRatio: scaleRatio });
}
export function InnerBoxplot(props) {
    const { scaleRatio } = props;
    const data = useBoxPlot();
    const { displayerKey } = useChartData();
    if (!data)
        return null;
    const { x, max, min, median, q1, q3, yDomain } = data;
    return (_jsxs("g", { clipPath: `url(#${displayerKey}clip-chart-1d)`, children: [_jsx(RenderAreaPath, { x: x, y1: max, y2: min, color: "black", scaleRatio: scaleRatio, yDomain: yDomain }), _jsx(RenderAreaPath, { x: x, y1: q3, y2: q1, color: "black", scaleRatio: scaleRatio, yDomain: yDomain }), _jsx(RenderPath, { x: x, y: median, color: "black", scaleRatio: scaleRatio, yDomain: yDomain })] }));
}
function RenderAreaPath(props) {
    const { x, y1, y2, scaleRatio, color, yDomain } = props;
    const areaPath = useAreaPath({ x, y1, y2 }, scaleRatio, yDomain);
    return _jsx("path", { d: areaPath, fill: color, opacity: 0.2 });
}
function RenderPath(props) {
    const { x, y, scaleRatio, color, yDomain } = props;
    const areaPath = usePath({ x, y }, scaleRatio, yDomain);
    return _jsx("path", { d: areaPath, fill: "transparent", stroke: color });
}
